import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import debounce from 'lodash/debounce';
import unescape from 'lodash/unescape';
import { Table } from 'react-virtualized';
import SubtitleIconLeftColumn from './SubtitleIconLeftColumn';
import SubtitleIconRightColumn from './SubtitleIconRightColumn';
import SubtitleTextarea from './SubtitleTextArea';
import SubtitleTimeSection from './SubtitleTimeSection';
import SubtitlesStyle from './SubtitlesStyle';

const ARROW_DOWN = 'ArrowDown';
const ARROW_UP = 'ArrowUp';

export default function Subtitles({
    currentIndex,
    subtitle,
    setSubtitle,
    checkSub,
    player,
    updateSub,
    updateSubIndex,
    translate,
    sub,
    removeSub,
    mergeSub,
    viewEng,
    bookmarked,
    setBookmarked,
    subtitleComment,
    setSubtitleComment,
    numEditors,
    selectedLng,
    setCurrentTime,
    // Add these props to receive from parent
    searchTerm,
    matchOffsets,
    currentMatchIndex,
}) {
    const [height, setHeight] = useState(100);
    const [anchorIndex, setAnchorIndex] = useState(null);
    const [isCommentBoxOpen, setCommentBoxOpen] = React.useState(false);
    const [commentText, setCommentText] = React.useState('');
    const [selectedSubtitle, setSelectedSubtitle] = useState(null); // Add this state
    
    const [containerWidth, setContainerWidth] = useState(window.innerWidth - 40);
    const containerRef = useRef(null);
    const resizeListenerRef = useRef(null); // Store the debounced resize function

    // Memoize the resize function properly with useCallback
    const resize = useCallback(() => {
        setHeight(document.body.clientHeight - 300);

        // Update width based on container size if available, otherwise use window width
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        } else {
            setContainerWidth(window.innerWidth - 40);
        }
    }, []);

    // Properly memoize the handleKeyPress function
    const handleKeyPress = useCallback((e) => {
        if (e.ctrlKey && e.key.toLowerCase() === 's') {
            e.preventDefault();
            document.getElementById('savebtn')?.click();
        }
    }, []);

    const handleEditableListKeyDown = useCallback((e) => {
        if (e.target.matches('.itemsalist textarea')) {
            let currentNode = e.target.closest('.itemsalist');
            let currentElement = currentNode.querySelector('textarea[data-field="text2"]');

            const insideTextAreaOnly = checkIfUpDownArrowNeedsToNavigateInside(currentElement, e.key);
            if (insideTextAreaOnly) {
                currentElement.click();
                return;
            }
            let nextNode = currentNode.nextElementSibling;
            let prevNode = currentNode.previousElementSibling;

            if (nextNode !== null && e.key === ARROW_DOWN) {
                let nextlele = nextNode.querySelector('textarea[data-field="text2"]');

                nextlele.focus();
                nextlele.setSelectionRange(0, 0);
                nextlele.click();
            }

            if (prevNode !== null && e.key === ARROW_UP && !currentNode.classList.contains('item_1')) {
                let prevele = prevNode.querySelector('textarea[data-field="text2"]');
                prevele.focus();
                prevele.setSelectionRange(prevele.value.length, prevele.value.length);
                prevele.click();
            }
        }
    }, []);

    // Define removeLocalStorageItem outside of useEffect so it's accessible everywhere
    const removeLocalStorageItem = useCallback(() => {
        localStorage.removeItem('subtitle');
    }, []);

    // Fix the event listener setup and cleanup
    useEffect(() => {
        // Initial resize
        resize();
        
        // Create a debounced resize function that persists between renders
        const debounceResize = debounce(resize, 500);
        resizeListenerRef.current = debounceResize;
        
        // Add event listeners
        window.addEventListener('resize', debounceResize);
        
        
        // Store functions in refs to ensure we remove the same function instances
        const editorElement = document.querySelector('#editable-list');
        if (editorElement) {
            editorElement.addEventListener('keydown', handleEditableListKeyDown);
        }
        
        window.addEventListener('beforeunload', removeLocalStorageItem);
        document.addEventListener('keydown', handleKeyPress);
        
        // Cleanup function - make sure to remove the same instances of event listeners
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('resize', debounceResize); // Use the same function instance
            
            if (editorElement) {
                editorElement.removeEventListener('keydown', handleEditableListKeyDown);
            }
            
            window.removeEventListener('beforeunload', removeLocalStorageItem);
        };
    }, [resize, handleKeyPress, handleEditableListKeyDown, player, removeLocalStorageItem]); // Add removeLocalStorageItem to dependencies

    const calculateCharsPerLine = function (textarea) {
        // Get the width of the textarea
        const textareaWidth = textarea.clientWidth;

        // Create a temporary element to measure the average character width
        const tempElement = document.createElement('span');
        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.fontSize = '16px'; // Match the font size of the textarea
        tempElement.style.fontFamily = window.getComputedStyle(textarea).fontFamily;
        tempElement.innerText = 'ABadi .'; // Use a single character to measure width
        const innerTextWidth = tempElement.innerText.length;
        document.body.appendChild(tempElement);

        const charWidth = tempElement.clientWidth / innerTextWidth;
        document.body.removeChild(tempElement);

        // Calculate the number of characters per line
        const charsPerLine = Math.floor(textareaWidth / charWidth);
        return charsPerLine;
    };

    const checkIfUpDownArrowNeedsToNavigateInside = useCallback((textarea, key) => {
        const charsPerLine = calculateCharsPerLine(textarea);
        //console.log(`Number of characters per line: ${charsPerLine}`);
        const numberOfChars = textarea.value.length;
        //console.log(`Number of characters: ${numberOfChars}`);
        const cursorPosition = textarea.selectionStart;
        //console.log(`Cursor position: ${cursorPosition}`);

        if (cursorPosition === numberOfChars && key === ARROW_DOWN) {
            return false;
        }

        let cursorOnLine = Math.ceil(cursorPosition / charsPerLine);
        // store length of each new line in an array
        const newLineLengths = textarea.value.split('\n').map((line) => line.length);
        //console.log(`Length of each new line: ${newLineLengths}`);
        //console.log(`Number of lines in each new line: ${newLineNumberOfLines}`)

        // Need to handle when there is new line in the text area
        let lineLengths = [];
        // repeat for each newLineLengths
        for (let i = 0; i < newLineLengths.length; i++) {
            if (newLineLengths[i] <= charsPerLine) {
                lineLengths.push(newLineLengths[i]);
            } else {
                lineLengths.push(charsPerLine);
                lineLengths.push(newLineLengths[i] - charsPerLine);
            }
        }

        //console.log(`Length of each line: ${lineLengths}`);

        let startLine = 0;
        let endLine = lineLengths[0];
        for (let i = 0; i < lineLengths.length; i++) {
            if (cursorPosition > startLine && cursorPosition <= endLine) {
                cursorOnLine = i;
                break;
            }
            if (i < lineLengths.length - 1) {
                startLine = endLine + 1;
                endLine = endLine + lineLengths[i + 1];
            }
        }
        //console.log(`Cursor on line,: ${cursorOnLine}`);

        //Do not go to next element if the cursor is not on the last line of the current node
        if (cursorOnLine < lineLengths.length && key === ARROW_DOWN) {
            return true;
        }

        //Do not go to previous element if the cursor is not on the first line of the current node
        if (cursorOnLine > 0 && key === ARROW_UP) {
            return true;
        }

        return false;
    }, []);

    

    const timeToSeconds = useCallback((time) => {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    }, []);

    const secondsToTime = useCallback((seconds) => {
        const hours = Math.floor(seconds / 3600)
            .toString()
            .padStart(2, '0');
        const minutes = Math.floor((seconds % 3600) / 60)
            .toString()
            .padStart(2, '0');
        const secs = Math.floor(seconds % 60)
            .toString()
            .padStart(2, '0');
        const millisecs = Math.floor((seconds % 1) * 1000)
            .toString()
            .padStart(3, '0');
        return `${hours}:${minutes}:${secs}.${millisecs}`;
    }, []);

    function moveBeforeEnd(time, subtitleEndTime) {
        // This is to test the condition when time === subtitleEndTime
        if (time === subtitleEndTime) {
            return time - 0.001;
        }
        return time;
    }

    const handleOnMouseClick = useCallback((e, index, field, playerArg, subtitleArg) => {
        //console.log('handleOnMouseClick', e, index, field);
        if (field === 'text' || field === 'text2') {
            //console.log('handleOnMouseClick', e, index, field);
            if (!isCommentBoxOpen) {
                const videoElement = playerArg.element;
                if (videoElement) {
                    if (videoElement.paused) {
                        videoElement.pause();
                    } else {
                        videoElement.play();
                    }

                    const text = e.target.value;
                    const startTime = subtitleArg[index].start;
                    const endTime = subtitleArg[index].end;

                    const startInSeconds = timeToSeconds(startTime);
                    const endInSeconds = timeToSeconds(endTime);

                    if (playerArg.element.duration >= startInSeconds) {
                        const caretOffset = e.target.selectionStart;
                        let percentageBeforeCaret = 0.0;
                        if (text.length > 0) {
                            percentageBeforeCaret = caretOffset / text.length;
                        } else {
                            percentageBeforeCaret = 0.5;
                        }
                        const totalDuration = endInSeconds - startInSeconds;
                        const durationBeforeCaret = totalDuration * percentageBeforeCaret;

                        const useNewTime = moveBeforeEnd(startInSeconds + durationBeforeCaret, endInSeconds);
                        playerArg.updateTime(useNewTime);
                        console.log(`Seeking to time - handleOnMouseClick: ${useNewTime}`);
                    }
                }
            }
        }
    }, []);

    const handleKeyDown = useCallback((e, index, field, playerArg, subtitleArg, selectedLanguageArg) => {
        if (e.shiftKey && e.key === 'Enter') {
            e.preventDefault();
            if (field === 'text' || field === 'text2') {
                if (selectedLanguageArg === 'English') {
                    const text = e.target.value;
                    const caretOffset = e.target.selectionStart;
                    const newTextBefore = text.substring(0, caretOffset).trim();
                    const newTextAfter = text.substring(caretOffset).trim();
                    let percentageBeforeCaret = 0.0;
                    if (text.length > 0) {
                        percentageBeforeCaret = caretOffset / text.length;
                    } else {
                        percentageBeforeCaret = 0.5;
                    }
                    // set focus to this element

                    const startTime = subtitleArg[index].start;
                    const endTime = subtitleArg[index].end;

                    const startInSeconds = timeToSeconds(startTime);
                    const endInSeconds = timeToSeconds(endTime);
                    const totalDuration = endInSeconds - startInSeconds;
                    const durationBeforeCaret = totalDuration * percentageBeforeCaret;

                    const newEndBefore = secondsToTime(startInSeconds + durationBeforeCaret);
                    const newStartAfter = secondsToTime(startInSeconds + durationBeforeCaret);

                    const newItems = [
                        ...subtitleArg.slice(0, index),
                        {
                            ...subtitleArg[index],
                            text: newTextBefore,
                            text2: newTextBefore,
                            end: newEndBefore,
                        },
                        {
                            ...subtitleArg[index],
                            text: newTextAfter,
                            text2: newTextAfter,
                            start: newStartAfter,
                            end: endTime,
                            note: null,
                            bookmark: false,
                        },
                        ...subtitleArg.slice(index + 1),
                    ];

                    console.log(`Seeking to time - handleKeyDown - 1: ${startInSeconds}`);

                    playerArg.updateTime(startInSeconds);
                    setSubtitle(newItems);
                } else {
                    const text = e.target.value;
                    const caretOffset = e.target.selectionStart;
                    const newTextBefore = text.substring(0, caretOffset).trim();
                    const newTextAfter = text.substring(caretOffset).trim();
                    let percentageBeforeCaret = 0.0;
                    if (text.length > 0) {
                        percentageBeforeCaret = caretOffset / text.length;
                    } else {
                        percentageBeforeCaret = 0.5;
                    }

                    const startTime = subtitleArg[index].start;
                    const endTime = subtitleArg[index].end;

                    const startInSeconds = timeToSeconds(startTime);
                    const endInSeconds = timeToSeconds(endTime);
                    const totalDuration = endInSeconds - startInSeconds;
                    const durationBeforeCaret = totalDuration * percentageBeforeCaret;

                    const newEndBefore = secondsToTime(startInSeconds + durationBeforeCaret);
                    const newStartAfter = secondsToTime(startInSeconds + durationBeforeCaret);

                    const newItems = [
                        ...subtitleArg.slice(0, index),
                        {
                            ...subtitleArg[index],
                            text: newTextBefore,
                            text2: newTextBefore,
                            end: newEndBefore,
                            Etext: subtitleArg[index].Etext,
                            Etext2: subtitleArg[index].Etext2,
                        },
                        {
                            ...subtitleArg[index],
                            text: newTextAfter,
                            text2: newTextAfter,
                            start: newStartAfter,
                            end: endTime,
                            Estart: '00:00:00',
                            Eend: '00:00:00',
                            Etext: '',
                            Etext2: '',
                        },
                        ...subtitleArg.slice(index + 1),
                    ];

                    console.log(`Seeking to time - handleKeyDown - 2: ${startInSeconds}`);

                    playerArg.updateTime(startInSeconds);
                    setSubtitle(newItems);
                }
            }
        }
        handleEditableListKeyDown(e);
    }, []);

    const scrollTimeoutRef = useRef(null);

    useEffect(() => {
        if (currentMatchIndex === -1) {
            //console.log('currentMatchIndex is -1, no scrolling', currentMatchIndex);
            return;
        }

        const currentMatch = matchOffsets[currentMatchIndex];
        //console.log('currentMatch', currentMatch);
        if (currentMatch) {
            const scrollToRow = currentMatch.textIndex;
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
                document.querySelector(`#editable-list .item_${scrollToRow + 1}`)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 0);
        }

        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [currentMatchIndex]);

    // Modify the highlightSearchTerm function in the parent component to remove yellow highlighting
    // You can replace it with this implementation:
    const highlightSearchTerm = (text, searchTerm, index) => {
        if (!searchTerm || !text) return text;
        
        // We'll still provide visual highlighting but make it more subtle
        const searchRegExp = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        
        // Return text with highlighted spans
        return text.replace(searchRegExp, (match) => {
            // Store match information for selection when navigating
            const matchInfo = {
                textIndex: index,
                match: match,
                // We'll calculate start/end positions later when needed
            };
            
            return match;
        });
    };

    const handleTextChange = (index, newText) => {
        const newData = [...subtitle];
        if (newData[index]?.text2 != undefined) {
            newData[index].text2 = newText;
        }
        setSubtitle(newData);
    };

    // Add this function to your Subtitles component
    const focusAndSelectMatch = useCallback((matchIndex) => {
        if (!matchOffsets || !matchOffsets[matchIndex]) return;
        
        const match = matchOffsets[matchIndex];
        const textareaId = `subtitleTextarea_${match.textIndex}`;
        
        // Use a small timeout to ensure rendering is complete
        setTimeout(() => {
            const textarea = document.querySelector(`#editable-list .item_${match.textIndex + 1} textarea[data-field="text2"]`);
            
            if (textarea) {
                // First focus the textarea
                textarea.focus();
                
                // Then select the text at the match position
                textarea.setSelectionRange(match.start, match.end);
                
                // Scroll into view if needed
                const container = document.querySelector('#editable-list');
                if (container) {
                    const row = document.querySelector(`#editable-list .item_${match.textIndex + 1}`);
                    if (row) {
                        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }
        }, 50);
    }, [matchOffsets]);

    // Add this effect to run when currentMatchIndex changes
    useEffect(() => {
        if (currentMatchIndex >= 0 && matchOffsets && matchOffsets.length > 0) {
            focusAndSelectMatch(currentMatchIndex);
        }
    }, [currentMatchIndex, focusAndSelectMatch]);

    // Fix the handleSubtitleClick function
    const handleSubtitleClick = useCallback((subtitleId) => {
        setSelectedSubtitle(subtitleId);
    }, []);

    // Make sure this gets cleaned up on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Process subtitles with useMemo to avoid recreating on every render
    const processedSubtitles = useMemo(() => {
        return subtitle.map(sub => ({
            ...sub,
            // Additional processing if needed
        }));
    }, [subtitle]);

    // In your Subtitles component, add this useEffect
    useEffect(() => {
        const handleResize = debounce(() => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
            setHeight(window.innerHeight - (window.innerWidth < 768 ? 400 : 300));
        }, 200);
        
        // Initial sizing
        handleResize();
        
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const [lastKnownGoodIndex, setLastKnownGoodIndex] = useState(0);

    // Add this function inside your Subtitles component to calculate the proper scroll index
    const getTargetScrollIndex = useMemo(() => {
        // Use search match index if available, otherwise use current subtitle index
        const baseIndex = matchOffsets[currentMatchIndex]?.textIndex !== undefined 
            ? matchOffsets[currentMatchIndex]?.textIndex 
            : currentIndex;
            
         if (baseIndex === undefined || baseIndex === null || baseIndex === -1) {
            // If we're in a gap (no current subtitle), use the last known good index
            // You might need to track this as a state variable
            return lastKnownGoodIndex || 0;
        }
        
        // Save this as the last known good index
        setLastKnownGoodIndex(baseIndex);

        // Calculate target index with offset to center current item
        const targetIndex = Math.min(
            // Don't go beyond the last possible index
            Math.max(0, subtitle.length - 1),
            // Try to scroll to baseIndex + 2 for centering
            Math.max(0, baseIndex + 2)
        );
        
        return targetIndex;
    }, [currentIndex, currentMatchIndex, matchOffsets, subtitle.length]);


    return (
        <>
            <div 
                style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
                ref={containerRef}
            >
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    {selectedLng === 'English' || selectedLng === 'Gujarati' ? (
                        <>
                            <div style={{ flex: '0 0 68px', maxWidth: '68px' }}></div>
                            <div style={{ flex: '0 0 105px', maxWidth: '105px', textAlign: 'center' }}>CPS/CPL</div>
                            <div style={{ flex: '0 0 100%', textAlign: 'center' }}>
                                Original Text
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ flex: '0 0 68px', maxWidth: '68px' }}></div>
                            <div style={{ flex: '0 0 105px', maxWidth: '105px', textAlign: 'center' }}>CPS/CPL</div>
                            <div style={{ flex: '0 0 50%', textAlign: 'center' }}>
                                Original Text
                            </div>
                            <div style={{ flex: '0 0 105px', maxWidth: '105px', textAlign: 'center' }}>CPS/CPL</div>
                            <div style={{ flex: '0 0 50%', textAlign: 'center' }}>
                                Translated Text
                            </div>
                        </>
                    )}
                </div>
                <SubtitlesStyle className="subtitles">
                    <Table
                        id="editable-list"
                        headerHeight={20}
                        width={containerWidth}
                        height={height}
                        rowHeight={window.innerWidth < 768 ? 80 : 120}
                        rowCount={subtitle.length}
                        rowGetter={({ index }) => subtitle[index]}
                        scrollToIndex={getTargetScrollIndex}
                        headerRowRenderer={() => null}
                        rowRenderer={(props) => {
                            // Update row style to use full width and adjustable height
                            const enhancedStyle = {
                                ...props.style,
                                height: window.innerWidth < 768 ? '80px' : '120px',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'stretch'
                            };
                            
                            return (
                                <div
                                    key={props.key}
                                    className={`${props.className} itemsalist item_${props.index + 1} focusinde_${props.index}`}
                                    style={enhancedStyle}
                                >
                                    <div className="item">
                                        <SubtitleIconLeftColumn
                                            index={props?.index}
                                            rowData={props?.rowData}
                                            updateSubIndex={updateSubIndex}
                                            mergeSub={mergeSub}
                                        />
                                        <SubtitleIconRightColumn
                                            index={props?.index}
                                            rowData={props?.rowData}
                                            updateSub={updateSub}
                                            removeSub={removeSub}
                                        />

                                        {selectedLng === 'English' || selectedLng === 'Gujarati' ? (
                                            <>
                                                <SubtitleTimeSection
                                                    rowData={props?.rowData}
                                                    index={props?.index}
                                                    currentIndex={currentIndex}
                                                    viewEng={viewEng}
                                                    checkSub={checkSub}
                                                    handleKeyDown={handleKeyDown}
                                                    updateSub={updateSub}
                                                    startField="start"
                                                    endField="end"
                                                    startValue={props?.rowData?.start}
                                                    endValue={props?.rowData?.end}
                                                />

                                                <SubtitleTextarea
                                                    index={props?.index}
                                                    rowData={props?.rowData}
                                                    currentIndex={currentIndex}
                                                    checkSub={checkSub}
                                                    highlightSearchTerm={highlightSearchTerm}
                                                    searchTerm={searchTerm}
                                                    currentMatchIndex={currentMatchIndex}
                                                    matchOffsets={matchOffsets}
                                                    handleKeyDown={handleKeyDown}
                                                    handleOnMouseClick={handleOnMouseClick}
                                                    updateSub={updateSub}
                                                    handleTextChange={handleTextChange}
                                                    player={player}
                                                    subtitle={subtitle}
                                                    selectedLanguage={selectedLng}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <SubtitleTimeSection
                                                    rowData={props?.rowData}
                                                    index={props?.index}
                                                    currentIndex={currentIndex}
                                                    viewEng={viewEng}
                                                    checkSub={checkSub}
                                                    handleKeyDown={handleKeyDown}
                                                    updateSub={updateSub}
                                                    startField="Estart"
                                                    endField="Eend"
                                                    startValue={props?.rowData?.Estart}
                                                    endValue={props?.rowData?.Eend}
                                                />

                                                <textarea
                                                    style={{ fontSize: '16px' }}
                                                    spellCheck="true"
                                                    className={[
                                                        'textarea ',
                                                        currentIndex === props.index ? 'highlight' : '',
                                                        checkSub(props.rowData) ? 'illegal' : '',
                                                    ]
                                                        .join(' ')
                                                        .trim()}
                                                    value={unescape(props.rowData.Etext)}
                                                />

                                                <SubtitleTimeSection
                                                    rowData={props?.rowData}
                                                    index={props?.index}
                                                    currentIndex={currentIndex}
                                                    viewEng={viewEng}
                                                    checkSub={checkSub}
                                                    handleKeyDown={handleKeyDown}
                                                    updateSub={updateSub}
                                                    startField="start"
                                                    endField="end"
                                                    startValue={props?.rowData?.start}
                                                    endValue={props?.rowData?.end}
                                                />

                                                <SubtitleTextarea
                                                    index={props?.index}
                                                    rowData={props?.rowData}
                                                    currentIndex={currentIndex}
                                                    checkSub={checkSub}
                                                    highlightSearchTerm={highlightSearchTerm}
                                                    searchTerm={searchTerm}
                                                    currentMatchIndex={currentMatchIndex}
                                                    matchOffsets={matchOffsets}
                                                    handleKeyDown={handleKeyDown}
                                                    handleOnMouseClick={handleOnMouseClick}
                                                    updateSub={updateSub}
                                                    handleTextChange={handleTextChange}
                                                    player={player}
                                                    subtitle={subtitle}
                                                    selectedLanguage={selectedLng}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        }}
                    ></Table>
                </SubtitlesStyle>
            </div>
        </>
    );
}
